import { useState } from "react";
import dayjs from "dayjs";
import { toast } from "sonner";
import { Eye, Plus, RefreshCw, Users, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  useApproveDelegation,
  useCancelDelegation,
  useRequestDelegationChanges,
  useResendDelegation,
  useUpdateRecipients,
} from "@/hooks";
import type { FormDelegation } from "@/types";

interface DelegationBannerProps {
  delegation: FormDelegation;
  applicantProcessId: string;
  formId: string;
  /** Pulls the submitted answers into the shared answer store for review. */
  onLoadAnswers: (delegation: FormDelegation) => void;
  /** Present when the step routes to a single staff member the owner must pick. */
  nextStaffId?: string;
  requiresNextStaff?: boolean;
  onApproved?: () => void;
}

const time = (v?: string | null) =>
  v ? dayjs(v).format("MMM D, YYYY, h:mm A") : "";

const DelegationBanner: React.FC<DelegationBannerProps> = ({
  delegation,
  applicantProcessId,
  formId,
  onLoadAnswers,
  nextStaffId,
  requiresNextStaff,
  onApproved,
}) => {
  const { t } = useTranslation();

  const approve = useApproveDelegation(applicantProcessId, formId);
  const requestChanges = useRequestDelegationChanges(applicantProcessId, formId);
  const resend = useResendDelegation(applicantProcessId, formId);
  const cancel = useCancelDelegation(applicantProcessId, formId);
  const updateRecipients = useUpdateRecipients(applicantProcessId, formId);

  const [changesOpen, setChangesOpen] = useState(false);
  const [changesComment, setChangesComment] = useState("");
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [rows, setRows] = useState<
    { email: string; name: string; role: "EDITOR" | "VIEWER" }[]
  >([]);

  const openManage = () => {
    setRows(
      delegation.recipients.map((r) => ({
        email: r.email,
        name: r.name ?? "",
        role: r.role,
      }))
    );
    setManageOpen(true);
  };

  const setRowEditor = (i: number) =>
    setRows((prev) =>
      prev.map((r, idx) => ({ ...r, role: idx === i ? "EDITOR" : "VIEWER" }))
    );

  const handleSaveRecipients = async () => {
    const recipients = rows
      .map((r) => ({
        email: r.email.trim().toLowerCase(),
        name: r.name.trim() || undefined,
        role: r.role,
      }))
      .filter((r) => r.email);
    if (recipients.filter((r) => r.role === "EDITOR").length !== 1) {
      toast.error(t("delegation.owner.oneEditorRule"));
      return;
    }
    try {
      await updateRecipients.mutateAsync({ id: delegation.id, recipients });
      toast.success(t("delegation.owner.recipientsUpdated"));
      setManageOpen(false);
    } catch (e) {
      err("delegation.owner.recipientsUpdateFailed")(e);
    }
  };

  const isSubmitted = delegation.status === "SUBMITTED";
  const editor = delegation.recipients.find((r) => r.role === "EDITOR");

  const err = (fallbackKey: string) => (e: any) => {
    const msg = e?.response?.data?.message;
    toast.error(typeof msg === "string" ? msg : t(fallbackKey));
  };

  const handleLoad = () => {
    onLoadAnswers(delegation);
    setLoaded(true);
    toast.success(t("delegation.owner.answersLoaded"));
  };

  const handleApprove = async () => {
    if (requiresNextStaff && !nextStaffId) {
      toast.error(t("delegation.owner.selectNextStaff"));
      return;
    }
    try {
      await approve.mutateAsync({ id: delegation.id, nextStaffId });
      toast.success(t("delegation.owner.approved"));
      onApproved?.();
    } catch (e) {
      err("delegation.owner.approveFailed")(e);
    }
  };

  const handleRequestChanges = async () => {
    if (!changesComment.trim()) return;
    try {
      await requestChanges.mutateAsync({
        id: delegation.id,
        comment: changesComment.trim(),
      });
      toast.success(t("delegation.owner.changesRequested"));
      setChangesOpen(false);
      setChangesComment("");
    } catch (e) {
      err("delegation.owner.requestChangesFailed")(e);
    }
  };

  const handleResend = async (recipientId: string) => {
    try {
      await resend.mutateAsync({ id: delegation.id, recipientId });
      toast.success(t("delegation.owner.resent"));
    } catch (e) {
      err("delegation.owner.resendFailed")(e);
    }
  };

  const handleCancel = async () => {
    try {
      await cancel.mutateAsync({
        id: delegation.id,
        reason: cancelReason.trim() || undefined,
      });
      toast.success(t("delegation.owner.cancelled"));
      setCancelOpen(false);
      setCancelReason("");
    } catch (e) {
      err("delegation.owner.cancelFailed")(e);
    }
  };

  return (
    <div
      className={`mx-4 mb-4 rounded border p-3 text-sm ${
        isSubmitted
          ? "border-emerald-300 bg-emerald-50"
          : "border-sky-300 bg-sky-50"
      }`}
    >
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <span className="font-medium text-gray-900">
          {isSubmitted
            ? t("delegation.owner.bannerSubmittedTitle")
            : t("delegation.owner.bannerPendingTitle")}
        </span>
        <span className="text-xs text-gray-500">
          {isSubmitted
            ? t("delegation.owner.submittedAt", { time: time(delegation.submittedAt) })
            : t("delegation.owner.sentAt", { time: time(delegation.createdAt) })}
        </span>
      </div>

      <div className="mb-3 space-y-1">
        <span className="text-xs uppercase tracking-wide text-gray-500">
          {t("delegation.owner.delegatedTo")}
        </span>
        <ul className="space-y-1">
          {delegation.recipients.map((r) => (
            <li key={r.id} className="flex items-center gap-2 text-gray-800">
              <span>{r.name || r.email}</span>
              <span className="rounded bg-white px-1.5 py-0.5 text-xs text-gray-500">
                {r.role === "EDITOR"
                  ? t("delegation.owner.editorLabel")
                  : t("delegation.owner.viewerLabel")}
              </span>
              {r.role === "VIEWER" && (
                <span className="text-gray-400">
                  <Eye className="h-3.5 w-3.5" />
                </span>
              )}
              {!isSubmitted && r.role === "EDITOR" && (
                <button
                  type="button"
                  onClick={() => handleResend(r.id)}
                  disabled={resend.isPending}
                  className="flex items-center gap-1 text-xs text-darkBlue"
                >
                  <RefreshCw className="h-3 w-3" />
                  {t("delegation.owner.resend")}
                </button>
              )}
            </li>
          ))}
        </ul>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {isSubmitted && (
          <>
            <button
              type="button"
              className="rounded border border-darkBlue px-3 py-1.5 text-xs text-darkBlue"
              onClick={handleLoad}
            >
              {t("delegation.owner.loadAnswers")}
            </button>
            <button
              type="button"
              className="main-dark-button text-xs"
              onClick={handleApprove}
              disabled={approve.isPending}
            >
              {approve.isPending
                ? t("delegation.owner.approving")
                : t("delegation.owner.approve")}
            </button>
            <button
              type="button"
              className="rounded border border-amber-500 px-3 py-1.5 text-xs text-amber-700"
              onClick={() => setChangesOpen(true)}
            >
              {t("delegation.owner.requestChanges")}
            </button>
          </>
        )}
        {!isSubmitted && (
          <button
            type="button"
            className="flex items-center gap-1 rounded border border-gray-300 px-3 py-1.5 text-xs text-gray-600"
            onClick={openManage}
          >
            <Users className="h-3.5 w-3.5" />
            {t("delegation.owner.manageRecipients")}
          </button>
        )}
        <button
          type="button"
          className="rounded border border-gray-300 px-3 py-1.5 text-xs text-gray-600"
          onClick={() => setCancelOpen(true)}
        >
          {t("delegation.owner.cancelDelegation")}
        </button>
        {loaded && isSubmitted && (
          <span className="text-xs text-emerald-700">
            {t("delegation.owner.answersLoaded")}
          </span>
        )}
      </div>

      <Dialog open={changesOpen} onOpenChange={setChangesOpen}>
        <DialogContent className="max-w-[520px] sm:rounded-md">
          <DialogHeader>
            <DialogTitle>{t("delegation.owner.requestChangesTitle")}</DialogTitle>
            <DialogDescription>
              {t("delegation.owner.requestChangesDescription")}
            </DialogDescription>
          </DialogHeader>
          <textarea
            className="main-input min-h-[110px] w-full"
            placeholder={t("delegation.owner.requestChangesPlaceholder")}
            value={changesComment}
            onChange={(e) => setChangesComment(e.target.value)}
          />
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              className="rounded border px-4 py-2 text-sm"
              onClick={() => setChangesOpen(false)}
            >
              {t("delegation.cancel")}
            </button>
            <button
              type="button"
              className="main-dark-button text-sm"
              onClick={handleRequestChanges}
              disabled={requestChanges.isPending || !changesComment.trim()}
            >
              {requestChanges.isPending
                ? t("delegation.owner.requesting")
                : t("delegation.owner.requestChangesConfirm")}
            </button>
          </div>
          {editor && (
            <p className="text-xs text-gray-500">{editor.name || editor.email}</p>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent className="max-w-[520px] sm:rounded-md">
          <DialogHeader>
            <DialogTitle>{t("delegation.owner.cancelTitle")}</DialogTitle>
            <DialogDescription>
              {t("delegation.owner.cancelDescription")}
            </DialogDescription>
          </DialogHeader>
          <textarea
            className="main-input min-h-[90px] w-full"
            placeholder={t("delegation.owner.cancelReasonPlaceholder")}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
          />
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              className="rounded border px-4 py-2 text-sm"
              onClick={() => setCancelOpen(false)}
            >
              {t("delegation.owner.keep")}
            </button>
            <button
              type="button"
              className="rounded bg-red-600 px-4 py-2 text-sm text-white"
              onClick={handleCancel}
              disabled={cancel.isPending}
            >
              {t("delegation.owner.cancelConfirm")}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={manageOpen} onOpenChange={setManageOpen}>
        <DialogContent className="max-w-[600px] sm:rounded-md">
          <DialogHeader>
            <DialogTitle>{t("delegation.owner.manageTitle")}</DialogTitle>
            <DialogDescription>
              {t("delegation.owner.manageDescription")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {rows.map((row, i) => (
              <div
                key={i}
                className="flex flex-wrap items-center gap-2 rounded border border-gray-200 p-2"
              >
                <input
                  type="email"
                  className="main-input min-w-[170px] flex-1"
                  placeholder={t("delegation.owner.emailPlaceholder")}
                  value={row.email}
                  onChange={(e) =>
                    setRows((prev) =>
                      prev.map((r, idx) =>
                        idx === i ? { ...r, email: e.target.value } : r
                      )
                    )
                  }
                />
                <input
                  type="text"
                  className="main-input min-w-[130px] flex-1"
                  placeholder={t("delegation.owner.namePlaceholder")}
                  value={row.name}
                  onChange={(e) =>
                    setRows((prev) =>
                      prev.map((r, idx) =>
                        idx === i ? { ...r, name: e.target.value } : r
                      )
                    )
                  }
                />
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setRowEditor(i)}
                    className={`rounded px-2 py-1 text-xs ${
                      row.role === "EDITOR"
                        ? "bg-darkBlue text-white"
                        : "border border-gray-300 text-gray-600"
                    }`}
                  >
                    {t("delegation.owner.editor")}
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setRows((prev) =>
                        prev.map((r, idx) =>
                          idx === i ? { ...r, role: "VIEWER" } : r
                        )
                      )
                    }
                    className={`rounded px-2 py-1 text-xs ${
                      row.role === "VIEWER"
                        ? "bg-darkBlue text-white"
                        : "border border-gray-300 text-gray-600"
                    }`}
                  >
                    {t("delegation.owner.viewer")}
                  </button>
                </div>
                {rows.length > 1 && (
                  <button
                    type="button"
                    onClick={() =>
                      setRows((prev) => prev.filter((_, idx) => idx !== i))
                    }
                    className="text-gray-400 hover:text-red-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={() =>
                setRows((prev) => [...prev, { email: "", name: "", role: "VIEWER" }])
              }
              className="flex items-center gap-1 text-sm text-darkBlue"
            >
              <Plus className="h-4 w-4" />
              {t("delegation.owner.addRecipient")}
            </button>
            <p className="text-xs text-gray-500">
              {t("delegation.owner.oneEditorRule")}
            </p>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              className="rounded border px-4 py-2 text-sm"
              onClick={() => setManageOpen(false)}
            >
              {t("delegation.cancel")}
            </button>
            <button
              type="button"
              className="main-dark-button text-sm"
              onClick={handleSaveRecipients}
              disabled={updateRecipients.isPending}
            >
              {updateRecipients.isPending
                ? t("delegation.owner.savingRecipients")
                : t("delegation.owner.saveRecipients")}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DelegationBanner;
